import { FloorplanItemReference } from "@domain/project/floorplan-item-reference";
import { IsArrayOfInstancesOf } from "@utils/class-validator/class-validator-constraints";
import { Expose, Transform, Type } from "class-transformer";
import { IsNumber, IsString, IsUUID, ValidateNested } from "class-validator";
import { v4 as uuidv4 } from "uuid";

export class ProductItemGroup {
  @IsUUID()
  @Expose({ name: "id" })
  private readonly _id: string;

  @IsString()
  @Expose({ name: "name" })
  private _name: string;

  @IsNumber()
  @Expose({ name: "groupId" })
  private readonly _groupId: number;

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanItemReference)
  @Type(() => FloorplanItemReference)
  @Expose({ name: "references" })
  @Transform(({ value }: { value: FloorplanItemReference[] }) => value ?? [], { toClassOnly: true })
  private _references: FloorplanItemReference[] = [];

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  constructor(name: string, groupId: number, _floorplanIds: string[] = [], references: FloorplanItemReference[] = []) {
    this._id = uuidv4();
    this._name = name;
    this._groupId = groupId;
    this._references = references;
  }

  get id() {
    return this._id;
  }

  get name() {
    return this._name;
  }

  set name(name: string) {
    this._name = name;
  }

  get groupId() {
    return this._groupId;
  }

  /** Derived from references – no longer a separate stored field. */
  get floorplanIds(): string[] {
    return [...new Set(this._references.map((r) => r.floorplanId))];
  }

  get references(): FloorplanItemReference[] {
    return this._references;
  }

  addReference(reference: FloorplanItemReference) {
    this._references.push(reference);
  }

  addReferences(references: FloorplanItemReference[]) {
    for (const ref of references) {
      const alreadyExists = this._references.some(
        (r) => r.floorplanId === ref.floorplanId && r.floorplanProductItemId === ref.floorplanProductItemId,
      );
      if (!alreadyExists) {
        this._references.push(ref);
      }
    }
  }

  removeReference(floorplanId: string, floorplanProductItemId: string) {
    this._references = this._references.filter(
      (ref) => !(ref.floorplanId === floorplanId && ref.floorplanProductItemId === floorplanProductItemId),
    );
  }
}
